import React, { ChangeEvent, Component, ReactNode } from 'react';
import { DateVariableProperties, VariableProperties } from '../../../domain/datatype/VariablePropertiesDomains';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import { TransactionVariableJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import DataType from '../../../constant/DataType';
import { DataStructure } from '../../../domain/structure/DataStructureDomains';
import DataTypeSelectBoxComponent from '../../custom/DataTypeSelectBoxComponent';
import { RootState } from '../../../redux/store';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { changeEvent, changeEventIsUpdate } from '../../../redux/actions/ChannelActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import FormicaInput from 'components/custom/FormicaInput';
import RoleHelper from '../../../RoleHelper';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { isStringValid } from '../../../util/Generator';
import ComponentName from 'constant/ComponentName';
import AnimatedButton from '../../custom/AnimatedButton';
import { customConfirm } from '../../custom/CustomPopupConfirm';

class TransactionVariableCreatorComponent extends Component<Props, State> {
  public state: State = new State();

  public render(): ReactNode {
    const { properties, name, description } = this.state;
    return (
      <div className={'w-full flex flex-col lg:flex-row justify-around items-center mt-5 mb-3 gap-y-5 gap-x-5'}>
        <div className={'w-full flex'}>
          <FormicaInput
            onChange={(e: React.FormEvent<HTMLInputElement>) => this.onChangeVariableName(e)}
            value={name}
            name={'name'}
            label={LanguageUtil.get({ id: 'common.label.name', defaultMessage: 'Name' })}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.name', defaultMessage: 'name' })}
            type={'text'}
            disabled={!RoleHelper.canEdit('Transaction')}
            data-testid={`${ComponentName.TRANSACTION}_variable_creator_name_input`}
          />
        </div>
        <div className={'w-full group flex gap-x-5 gap-y-5'}>
          <DataTypeSelectBoxComponent
            ghost={false}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.dataType', defaultMessage: 'Data Type' })}
            properties={properties}
            onChangeValue={(e: FormicaSelectBoxItem) => this.onChangeDataType(e)}
            disabled={!RoleHelper.canEdit('Transaction')}
            data-testid={`${ComponentName.TRANSACTION}_variable_creator_datatype_selectbox`}
          />
        </div>
        {properties.dataType == DataType.DATE && (
          <span className='w-full'>
            <FormicaInput
              name={'Date Format'}
              label={LanguageUtil.get({ id: 'common.label.dateFormat', defaultMessage: 'DateFormat' })}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.dateFormat', defaultMessage: 'Date Format' })}
              type={'text'}
              value={(properties as DateVariableProperties).format}
              onChange={this.onChangeDateFormat}
              disabled={!RoleHelper.canEdit('Transaction')}
              data-testid={`${ComponentName.TRANSACTION}_variable_creator_dateformat_input`}
            />
          </span>
        )}
        <div className={'w-full rounded flex justify-between items-center'}>
          <span className={'flex items-center w-full  '}>
            <FormicaInput
              value={description}
              name={'displayName'}
              onChange={(e: React.FormEvent<HTMLInputElement>) => this.setState({ description: (e.target as HTMLInputElement).value })}
              type={'text'}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.displayName', defaultMessage: 'Display Name' })}
              disabled={!RoleHelper.canEdit('Transaction')}
              data-testid={`${ComponentName.TRANSACTION}_variable_creator_displayname_input`}
            />
          </span>
        </div>
        <div className={'h-full flex justify-center items-center'}>{this.getVariableActionButton()}</div>
      </div>
    );
  }

  private getVariableActionButton = () => {
    const { selections } = this.props;
    if (selections && selections.length > 0 && !this.isEventDeployed())
      return (
        <AnimatedButton
          text={LanguageUtil.get({ id: 'common.button.delete', defaultMessage: 'Delete' })}
          customType={'secondary'}
          customSize={'sm'}
          icon={'delete'}
          onClick={(e) => {
            customConfirm({
              target: e.currentTarget,
              accept: () => this.deleteVariables(),
              message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure remove this variables?' }),
            });
          }}
          disabled={!RoleHelper.canEdit('Transaction')}
          data-testid={`${ComponentName.TRANSACTION}_variable_creator_delete_button`}
        />
      );
    return (
      <AnimatedButton
        text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
        customType={'primary'}
        customSize={'sm'}
        icon={'add'}
          onClick={this.addVariable}
          disabled={!RoleHelper.canEdit('Transaction')}
        data-testid={`${ComponentName.TRANSACTION}_variable_creator_add_button`}
      />
    );
  };

  private isEventDeployed = () => {
    const {
      event: { event },
    } = this.props;
    return event.stage != 'DRAFT';
  };

  private onChangeDataType = (e: FormicaSelectBoxItem) => {
    const { properties } = this.state;
    if (e.properties?.dataType == DataType.DATE) {
      (properties as DateVariableProperties).format = 'epochMillis';
      properties.dataType = e.properties.dataType;
      this.setState({ properties });
    } else {
      this.setState({ properties: e.properties || { dataType: DataType.STRING } });
    }
  };

  private onChangeDateFormat = (e: ChangeEvent<HTMLInputElement>) => {
    const { properties } = this.state;
    if (properties.dataType == DataType.DATE) {
      (properties as DateVariableProperties).format = e.target.value;
      this.setState({ properties });
    }
  };

  private onChangeVariableName(e: React.FormEvent<HTMLInputElement>) {
    if (isStringValid(e.currentTarget.value)) {
      this.setState({ name: (e.target as HTMLInputElement).value });
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'messages.warning.specialCharacters', defaultMessage: 'Event variable name can not include special characters' }),
      });
    }
  }

  private addVariable = (): void => {
    const { name, description, properties } = this.state;
    const {
      event: { event },
      changeEvent,
    } = this.props;
    if ((properties as VariableProperties).dataType == undefined) {
      return MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.event', defaultMessage: 'Event' }),
        detail: (
          <div>
            <b>{event.name}</b>
            {LanguageUtil.get({ id: 'messages.cannotAdd2', defaultMessage: 'can not add' })}
          </div>
        ),
      });
    } else if (name != undefined && name.length == 0) {
      return MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.eventVariable', defaultMessage: 'Event Variable' }),
        detail: (
          <div data-testid='channel_variable_name_required'>
            {LanguageUtil.get({ id: 'messages.warning.variableNameNotNull', defaultMessage: 'Variable name can not null' })}
          </div>
        ),
      });
    } else if (name == 'actor' || name == 'event' || name == 'flow') {
      return MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.eventVariable', defaultMessage: 'Event Variable' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.warning.variableNameSpecialName', defaultMessage: 'Variable name can not be special name' })}</div>,
      });
    } else if (event.variables?.find((variable: any) => variable.name == name) != undefined) {
      return MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.eventVariable', defaultMessage: 'Event Variable' }),
        detail: (
          <div data-testid='channel_variable_already_exist'>
            {LanguageUtil.get({ id: 'messages.warning.variableNameExist', defaultMessage: 'Variable name already exist' })}
          </div>
        ),
      });
    } else {
      const variable: TransactionVariableJsonModel = {
        name: name.trim(),
        description: description,
        properties: properties,
      };
      event.variables.push(variable);
      changeEvent(event);
      this.setState({ name: '', properties: { dataType: DataType.STRING }, description: '' });
      return MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.eventVariable', defaultMessage: 'Event Variable' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.success.variable', defaultMessage: 'Variable created.' })}</div>,
      });
    }
  };

  private deleteVariables = (): void => {
    const { selections, removeEventVariables } = this.props;
    selections.forEach((variable: TransactionVariableJsonModel) => {
      removeEventVariables(variable);
    });
  };
}

class State {
  name: string = '';
  properties: VariableProperties = { dataType: DataType.STRING } as VariableProperties;
  description: string = '';
}

interface Props extends ReduxProps {
  dataStructures: Array<DataStructure>;
  selections: TransactionVariableJsonModel[];
  removeEventVariables: (variable: TransactionVariableJsonModel) => void;
}

const mapStateToProps = (state: RootState) => ({
  event: state.event,
  isUpdate: state.event.isUpdate,
});
const mapDispatchToProps = { enterComponent, leaveComponent, changeEvent, changeEventIsUpdate };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TransactionVariableCreatorComponent);
